import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ActionButton } from '@adobe/react-spectrum';
import LinkOut from '@spectrum-icons/workflow/LinkOut';

import { getPrettyCategoryLabel } from '../../../data/CategoryPrettyDisplayUtils';
import {
    deriveComboHeadline,
    deriveComboNotableChips,
    deriveComboRiskChips,
    deriveComboStrengthChips,
    comboEnumLabel,
    outcomeLabel,
} from '../../../data/deckComboProfileMarkers';
import { useOpenCardInfo } from '../../common/cardInfoDialog/useOpenCardInfo';
import { ExpandableRow } from '../../common/expandableRow/ExpandableRow';
import { useExclusiveExpansion } from '../../common/expandableRow/useExclusiveExpansion';
import { CombosProfilePanel } from './CombosProfilePanel';
import HeroPanel, { HeroPanelTotal } from '../../common/heroPanel/HeroPanel';
import './CombosPane.css';

// ---------- Wincon / enabler classification ---------------------------------
// Row classification mirrors backend DetailsCombos.js. Aggregates come from
// `deck.details.combos.profile` (post-processed), but per-row type / badges
// still read the list so that rows stay in lock-step with the combo cards
// the list surfaces.

const DIRECT_WINCON_CATEGORIES = new Set([
    'INFINITE-DAMAGE',
    'INFINITE-LIFELOSS',
    'INFINITE-COMBAT',
    'INFINITE-TOKENS-WITH-HASTE',
    'INFINITE-TOKENS',
    'OPPONENTS-LOCKED',
    'INFINITE-COUNTERS',
    'INFINITE-TURNS',
    'INFINITE-MILL',
    'INFINITE-EXILE',
    'WIN-GAME',
]);

const getCategoryId = (cat) => {
    if (typeof cat === 'string') return cat.toUpperCase();
    if (cat && typeof cat === 'object' && cat.id) return cat.id.toUpperCase();
    return null;
};

function isValidWincon(combo) {
    const cats = (combo?.categories || []).map(getCategoryId).filter(Boolean);
    for (const c of cats) {
        if (DIRECT_WINCON_CATEGORIES.has(c)) return true;
    }
    if (cats.includes('INFINITE-DRAW') && cats.includes('INFINITE-MANA')) return true;
    return false;
}

function classifyComboType(combo) {
    const raw = (!combo.type || combo.type === '' ? 'enabler' : combo.type).toUpperCase();
    if (raw === 'WINCON') return isValidWincon(combo) ? 'WINCON' : null;
    if (raw === 'LOCK') return 'LOCK';
    return 'ENABLER';
}

// ---------- Card resolution ----------------------------------------------

function resolveComboCard(cards, memberId) {
    if (!cards) return null;
    if (cards[memberId]) return cards[memberId];
    let retCard = null;
    Object.keys(cards).forEach((cardId) => {
        const card = cards[cardId];
        if (card?.containerId !== memberId) return;
        if (card.isFrontFace) {
            retCard = card;
        } else if (!retCard) {
            if (new RegExp(`_${memberId}`).test(cardId)) retCard = card;
            else if (new RegExp(`${memberId}_back`).test(cardId)) retCard = card;
        }
    });
    return retCard;
}

function hydrateCombo(combo, cards) {
    const members = (combo?.cards || []).map((id) => resolveComboCard(cards, id)).filter(Boolean);
    const type = classifyComboType(combo);
    return { ...combo, __members: members, __type: type };
}

// ---------- Display helpers ------------------------------------------------

const TYPE_META = {
    WINCON: { label: 'Wincon', tone: 'alert' },
    ENABLER: { label: 'Enabler', tone: 'gold' },
    LOCK: { label: 'Lock', tone: 'cool' },
};

const FILTERS = [
    { key: 'ALL', label: 'All' },
    { key: 'WINCON', label: 'Wincons' },
    { key: 'ENABLER', label: 'Enablers' },
    { key: 'LOCK', label: 'Locks' },
];

function formatComplexity(value) {
    if (value == null || Number.isNaN(value)) return '—';
    return `${(value * 100).toFixed(0)}%`;
}

function complexityTone(value) {
    if (value == null || Number.isNaN(value)) return 'neutral';
    if (value >= 0.65) return 'easy';
    if (value >= 0.4) return 'moderate';
    return 'hard';
}

// ---------- Pane -----------------------------------------------------------

export function CombosPane() {
    const deck = useSelector((s) => s.deck.deck);
    const cards = useSelector((s) => s.deck.cards);
    const openCardInfo = useOpenCardInfo();

    const [filter, setFilter] = useState('ALL');
    const { expandedId, toggle: toggleExpanded } = useExclusiveExpansion();

    const profile = deck?.details?.combos?.profile || null;
    const deckScore = deck?.details?.combos?.score;

    const [profilePanelOpen, setProfilePanelOpen] = useState(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
        return window.matchMedia('(min-width: 768px)').matches;
    });

    const combos = useMemo(() => {
        const list = deck?.details?.combos?.list || [];
        return list
            .map((c) => hydrateCombo(c, cards))
            .filter((c) => c.__type !== null)
            .sort((a, b) => {
                const scoreDiff = (b?.score || 0) - (a?.score || 0);
                if (scoreDiff !== 0) return scoreDiff;
                const bx = b?.complexity?.bias?.final ?? 0;
                const ax = a?.complexity?.bias?.final ?? 0;
                return bx - ax;
            });
    }, [deck, cards]);

    const headline = useMemo(() => deriveComboHeadline(profile), [profile]);
    const strengthChips = useMemo(() => deriveComboStrengthChips(profile), [profile]);
    const riskChips = useMemo(() => deriveComboRiskChips(profile), [profile]);
    const notableChips = useMemo(() => deriveComboNotableChips(profile), [profile]);

    const highlights = useMemo(() => {
        const raw = Array.isArray(profile?.highlights) ? profile.highlights : [];
        return raw
            .map((h) => ({
                ...h,
                __cards: (h.cardIds || [])
                    .map((id) => resolveComboCard(cards, id))
                    .filter(Boolean),
            }))
            .filter((h) => h.__cards.length > 0);
    }, [profile, cards]);

    const filterCounts = useMemo(() => {
        const fromProfile = profile
            ? {
                  ALL: profile.count || 0,
                  WINCON: profile.winconCount || 0,
                  ENABLER: profile.enablerCount || 0,
                  LOCK: profile.lockCount || 0,
              }
            : null;
        if (fromProfile && fromProfile.ALL > 0) return fromProfile;
        const fallback = { ALL: combos.length, WINCON: 0, ENABLER: 0, LOCK: 0 };
        combos.forEach((c) => {
            fallback[c.__type] = (fallback[c.__type] || 0) + 1;
        });
        return fallback;
    }, [profile, combos]);

    const filteredCombos = useMemo(() => {
        if (filter === 'ALL') return combos;
        return combos.filter((c) => c.__type === filter);
    }, [combos, filter]);

    const hasProfileContent = !!profile && (profile.count || 0) > 0;

    const handleSelectCard = (card) => {
        if (!card) return;
        openCardInfo(card);
    };

    return (
        <div className="combosPane">
            <ComboLinesHeader
                profile={profile}
                deckScore={deckScore}
                fallbackCounts={filterCounts}
                headline={headline}
                highlights={highlights}
                onSelectCard={handleSelectCard}
            />

            {hasProfileContent && (
                <section
                    className={`powerSection powerSection--collapsible${
                        profilePanelOpen ? ' is-open' : ''
                    }`}
                >
                    <button
                        type="button"
                        className="powerSectionToggle"
                        aria-expanded={profilePanelOpen}
                        aria-controls="combosProfilePanelBody"
                        onClick={() => setProfilePanelOpen((v) => !v)}
                    >
                        <span className="powerSectionEyebrow">
                            <span className="powerSectionChevron" aria-hidden="true">
                                {profilePanelOpen ? '▾' : '▸'}
                            </span>
                            Combo profile
                        </span>
                        <span className="powerSectionHint">
                            Strengths, risks, top combos, and the mechanical shape of the package.
                        </span>
                    </button>
                    {profilePanelOpen && (
                        <div id="combosProfilePanelBody" className="powerSectionBody">
                            <CombosProfilePanel
                                profile={profile}
                                headline={headline}
                                strengths={strengthChips}
                                risks={riskChips}
                                notable={notableChips}
                                highlights={highlights}
                                cards={cards}
                                onSelectCard={handleSelectCard}
                            />
                        </div>
                    )}
                </section>
            )}

            <div className="combosFilterRow" role="tablist" aria-label="Combo filter">
                {FILTERS.map((f) => {
                    const count = filterCounts[f.key] || 0;
                    const disabled = f.key !== 'ALL' && count === 0;
                    return (
                        <button
                            key={f.key}
                            type="button"
                            className={`combosFilterBtn ${filter === f.key ? 'is-active' : ''}`}
                            onClick={() => !disabled && setFilter(f.key)}
                            disabled={disabled}
                            role="tab"
                            aria-selected={filter === f.key}
                        >
                            <span className="combosFilterBtnLabel">{f.label}</span>
                            <span className="combosFilterBtnCount">{count}</span>
                        </button>
                    );
                })}
            </div>

            {filteredCombos.length === 0 ? (
                <div className="combosEmpty">
                    <span className="combosEmptyEyebrow">No combos</span>
                    <span className="combosEmptyBody">
                        {combos.length === 0
                            ? 'This deck has no detected combos. Pure value decks often show up here — or the card-pair patterns simply haven’t been indexed yet.'
                            : `No ${filter.toLowerCase()} combos match. Try another filter.`}
                    </span>
                </div>
            ) : (
                <div className="combosList">
                    {filteredCombos.map((combo) => {
                        const rowKey = `combo:${combo.id}`;
                        return (
                            <ComboRow
                                key={combo.id}
                                combo={combo}
                                expanded={expandedId === rowKey}
                                onToggle={() => toggleExpanded(rowKey)}
                                onSelectCard={handleSelectCard}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ---------- Header ---------------------------------------------------------
// Salt-personality-style hero — left column owns the name + headline + a
// quick row of top-combo chips; right column owns the combo-score totem and
// the opaque shape tags (density, redundancy, wincon isolation, deployment
// band). Flavor border accent tracks whether the deck has wincons, just
// enablers, or no combos at all.

function primaryHeadlineName({ profile, count, winconCount }) {
    if (count === 0) return 'No combos detected';
    if (profile?.primaryOutcome) return outcomeLabel(profile.primaryOutcome);
    if (winconCount > 0) return 'Combo finisher';
    return 'Combo engine';
}

function ComboLinesHeader({
    profile,
    deckScore,
    fallbackCounts,
    headline,
    highlights,
    onSelectCard,
}) {
    const score = profile?.score != null ? profile.score : deckScore;
    const count = profile?.count ?? fallbackCounts?.ALL ?? 0;
    const winconCount = profile?.winconCount ?? fallbackCounts?.WINCON ?? 0;

    const primaryName = primaryHeadlineName({ profile, count, winconCount });
    const flavor =
        count === 0 ? 'none' : winconCount > 0 ? 'wincons' : 'enablers';

    const secondaryTags = [
        profile?.density && profile.density !== 'none' && {
            key: 'density',
            label: `${comboEnumLabel('density', profile.density)} density`,
            family: 'density',
        },
        profile?.redundancy && {
            key: 'redundancy',
            label: comboEnumLabel('redundancy', profile.redundancy),
            family: `redundancy-${profile.redundancy}`,
        },
        profile?.speed?.deploymentBand &&
            profile.speed.deploymentBand !== 'none' && {
                key: 'deployment',
                label: `${comboEnumLabel('deploymentBand', profile.speed.deploymentBand)} deploy`,
                family: 'deployment',
            },
        profile?.payoff?.winconLineIsolation &&
            profile.payoff.winconLineIsolation !== 'none' && {
                key: 'isolation',
                label: `${comboEnumLabel(
                    'winconLineIsolation',
                    profile.payoff.winconLineIsolation
                )} wincon lines`,
                family: 'isolation',
            },
    ].filter(Boolean);

    const topCombos = (highlights || []).slice(0, 3);

    return (
        <HeroPanel
            className="comboLines"
            data-flavor={flavor}
            aria-label={primaryName}
            eyebrow="Combo lines"
            title={primaryName}
            hint={headline || undefined}
            aside={
                <>
                    <HeroPanelTotal
                        label="Combo score"
                        value={score != null ? Number(score).toFixed(0) : '—'}
                    />
                    {secondaryTags.length > 0 && (
                        <div className="comboLinesTags">
                            {secondaryTags.map((t) => (
                                <span
                                    key={t.key}
                                    className="comboLinesTag"
                                    data-family={t.family}
                                    title={t.label}
                                >
                                    {t.label}
                                </span>
                            ))}
                        </div>
                    )}
                </>
            }
        >
            {topCombos.length > 0 &&
                topCombos.map((h, i) => {
                    const firstCard = h.__cards[0];
                    const comboName = h.__cards
                        .map((c) => c?.name)
                        .filter(Boolean)
                        .join(' + ');
                    const canClick = !!(firstCard && onSelectCard);
                    return (
                        <button
                            key={h.comboId || `combo_${i}`}
                            type="button"
                            className="comboLinesTopCombo"
                            onClick={() => canClick && onSelectCard(firstCard)}
                            disabled={!canClick}
                            title={`${comboName} · ${Math.round(h.score || 0)} score`}
                        >
                            <span className="comboLinesTopComboName">{comboName}</span>
                            <span className="comboLinesTopComboScore">
                                {Math.round(h.score || 0)}
                            </span>
                        </button>
                    );
                })}
        </HeroPanel>
    );
}

// ---------- Per-combo row --------------------------------------------------

function ComboRow({ combo, expanded, onToggle, onSelectCard }) {
    const type = combo.__type;
    const typeMeta = TYPE_META[type] || { label: 'Combo', tone: 'gold' };
    const cats = (combo.categories || []).map(getCategoryId).filter(Boolean);
    const cx = combo?.complexity || {};
    const cxFinal = cx?.bias?.final;
    const cxTone = complexityTone(cxFinal);
    const extraCmc = cx?.additionalCmcValue;
    const flags = buildComboFlags(combo);
    const score = Number(combo?.score || 0);

    const sortedMembers = (combo.__members || []).slice().sort((a, b) =>
        (a?.name || '').localeCompare(b?.name || '')
    );

    const header = (
        <>
            <div className="comboCardHeaderMain">
                <div className="comboCardCardChips">
                    {sortedMembers.map((card, idx) => (
                        <ComboCardChip
                            key={`${combo.id}_${card?.id || idx}`}
                            card={card}
                            onSelectCard={onSelectCard}
                        />
                    ))}
                </div>
                <div className="comboCardTagRow">
                    <span className={`comboTypeTag comboTypeTag--${typeMeta.tone}`}>{typeMeta.label}</span>
                    {cats.slice(0, 6).map((cat) => (
                        <span key={cat} className="comboEffectTag">
                            {getPrettyCategoryLabel(cat)}
                        </span>
                    ))}
                    {flags.map((f) => (
                        <span key={f.key} className={`comboFlag comboFlag--${f.tone}`} title={f.hint}>
                            {f.label}
                        </span>
                    ))}
                </div>
            </div>
            <div className="comboCardHeaderMeta">
                <div className="comboCardMetric">
                    <span className="comboCardMetricLabel">Score</span>
                    <span className="comboCardMetricValue">{score.toFixed(0)}</span>
                </div>
                <div className={`comboCardMetric comboCardMetric--${cxTone}`}>
                    <span className="comboCardMetricLabel">Complexity</span>
                    <span className="comboCardMetricValue">{formatComplexity(cxFinal)}</span>
                </div>
                {extraCmc > 0 && (
                    <div className="comboCardMetric">
                        <span className="comboCardMetricLabel">Extra mana</span>
                        <span className="comboCardMetricValue">+{extraCmc}</span>
                    </div>
                )}
            </div>
        </>
    );

    return (
        <ExpandableRow
            expanded={expanded}
            onToggle={onToggle}
            tone={typeMeta.tone === 'alert' ? 'red' : typeMeta.tone === 'gold' ? 'gold' : 'cool'}
            rowClassName="comboCard"
            bodyClassName="comboCardBody"
            headerClassName="comboCardHeader"
            header={header}
        >
            <ComboCardBody combo={combo} />
        </ExpandableRow>
    );
}

function ComboCardChip({ card, onSelectCard }) {
    if (!card) return null;
    const name = card?.name || card?.id || 'Unknown';
    const handleClick = (e) => {
        e.stopPropagation();
        if (onSelectCard) onSelectCard(card);
    };
    return (
        <span
            className="comboCardChip"
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    if (onSelectCard) onSelectCard(card);
                }
            }}
            title={`View ${name}`}
        >
            {card?.imageUri ? (
                <img src={card.imageUri} alt={name} className="comboCardChipThumb" loading="lazy" />
            ) : (
                <span className="comboCardChipThumb comboCardChipThumb--empty" />
            )}
            <span
                className="comboCardChipName"
                style={{ color: card?.isFrontFace === false ? '#fff49a' : '#ffffff' }}
            >
                {name}
            </span>
        </span>
    );
}

function ComboCardBody({ combo }) {
    const cx = combo?.complexity || {};
    const steps = cx?.steps?.lines || [];
    const results = cx?.results?.lines || [];
    const easy = cx?.easyPrerequisites?.lines || [];
    const notable = cx?.notablePrerequisites?.lines || [];
    const preconditions = cx?.preconditions?.lines || [];
    const sections = cx?.bias?.sections || {};
    const spellbookUri = combo?.spellbookUri;

    return (
        <div className="comboCardBody">
            {(easy.length > 0 || notable.length > 0 || preconditions.length > 0) && (
                <ComboSection title="Prerequisites">
                    {preconditions.length > 0 && (
                        <ComboSubList
                            label="Must be true"
                            tone="alert"
                            lines={preconditions}
                        />
                    )}
                    {notable.length > 0 && (
                        <ComboSubList label="Notable" tone="watch" lines={notable} />
                    )}
                    {easy.length > 0 && (
                        <ComboSubList label="Easy" tone="ok" lines={easy} />
                    )}
                </ComboSection>
            )}

            {steps.length > 0 && (
                <ComboSection title="Step-by-step">
                    <ol className="comboStepList">
                        {steps.map((s, idx) => (
                            <li className="comboStep" key={idx}>
                                <span className="comboStepNum">{idx + 1}</span>
                                <span className="comboStepText">{s.parsed}</span>
                                {s.qualifierCount > 0 && (
                                    <span
                                        className="comboStepQualifier"
                                        title={buildQualifierHint(s.qualifiers)}
                                    >
                                        {buildQualifierLabel(s.qualifiers)}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ol>
                </ComboSection>
            )}

            {results.length > 0 && (
                <ComboSection title="Produces">
                    <ul className="comboResultList">
                        {results.map((r, idx) => (
                            <li className="comboResult" key={idx}>
                                {r.parsed}
                            </li>
                        ))}
                    </ul>
                </ComboSection>
            )}

            {Object.keys(sections).length > 0 && (
                <ComboSection title="Difficulty breakdown" hint="Per-section bias · higher bars = easier">
                    <div className="comboBiasGrid">
                        {Object.keys(sections).map((key) => {
                            const v = Number(sections[key] ?? 0);
                            return (
                                <div className="comboBiasRow" key={key}>
                                    <span className="comboBiasLabel">{humanizeSectionKey(key)}</span>
                                    <div className="comboBiasTrack">
                                        <div
                                            className="comboBiasFill"
                                            style={{ width: `${Math.max(2, Math.min(100, v * 100))}%` }}
                                        />
                                    </div>
                                    <span className="comboBiasValue">{formatComplexity(v)}</span>
                                </div>
                            );
                        })}
                    </div>
                </ComboSection>
            )}

            {spellbookUri && (
                <div className="comboCardBodyFooter">
                    <ActionButton
                        onPress={() => window.open(spellbookUri, '_blank', 'noopener')}
                        UNSAFE_className="comboSpellbookBtn"
                    >
                        <LinkOut />
                        <span>View on Commander Spellbook</span>
                    </ActionButton>
                </div>
            )}
        </div>
    );
}

function ComboSection({ title, hint, children }) {
    return (
        <section className="comboCardSection">
            <header className="comboCardSectionHeader">
                <span className="comboCardSectionTitle">{title}</span>
                {hint && <span className="comboCardSectionHint">{hint}</span>}
            </header>
            {children}
        </section>
    );
}

function ComboSubList({ label, tone, lines }) {
    return (
        <div className={`comboSubList comboSubList--${tone}`}>
            <span className="comboSubListLabel">{label}</span>
            <ul className="comboSubListItems">
                {lines.map((l, idx) => (
                    <li key={idx}>{l.parsed}</li>
                ))}
            </ul>
        </div>
    );
}

// ---------- Tiny helpers ---------------------------------------------------

function buildComboFlags(combo) {
    const flags = [];
    const cx = combo?.complexity || {};
    if (cx.requiresCardsOnBoard) {
        flags.push({
            key: 'board',
            label: 'Needs board state',
            tone: 'watch',
            hint: 'At least one piece must already be on the battlefield.',
        });
    }
    if (cx.requiresCommanderToBe) {
        flags.push({
            key: 'commander',
            label: `Commander: ${cx.requiresCommanderToBe}`,
            tone: 'cool',
            hint: 'Combo only works with this specific commander.',
        });
    }
    if (Array.isArray(cx.requiresKeywords) && cx.requiresKeywords.length > 0) {
        flags.push({
            key: 'keywords',
            label: `Needs ${cx.requiresKeywords.join(', ')}`,
            tone: 'watch',
            hint: 'Pieces of the combo need these keywords to function.',
        });
    }
    if (combo?.isManaSink) {
        flags.push({ key: 'manasink', label: 'Mana sink', tone: 'gold', hint: 'Acts as a sink for infinite mana.' });
    }
    if (combo?.templateMatched) {
        flags.push({
            key: 'template',
            label: 'Template match',
            tone: 'ok',
            hint: 'Detected via a generic pattern, not an exact Spellbook entry.',
        });
    }
    return flags;
}

function buildQualifierLabel(qualifiers) {
    if (!qualifiers) return '';
    const keys = Object.keys(qualifiers);
    if (!keys.length) return '';
    return keys
        .map((k) => k.replace(/^requires/, '').replace(/([A-Z])/g, ' $1').trim().toLowerCase())
        .join(' · ');
}

function buildQualifierHint(qualifiers) {
    const label = buildQualifierLabel(qualifiers);
    return label ? `Requires: ${label}` : '';
}

function humanizeSectionKey(key) {
    if (!key) return '';
    return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (c) => c.toUpperCase())
        .replace(/\s+/g, ' ')
        .trim();
}
